import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Page } from './../../shared/models/page';
import { Injectable } from '@angular/core';
import { Rsvp } from '../models/rsvp';
import { List } from 'immutable';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { RsvpHttpService } from './rsvp.http.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { ComparatorUtils } from '../utils/comparator-utils';
import { RoomStaySummary } from '../models/room-stay-summary';
import { TranslateService } from '@ngx-translate/core';
import { TransactionUtils } from '../utils/transaction-utils';
import { Constants } from '../../shared/constants';

@Injectable()
export class RsvpStore {
  private rsvpSubject: ReplaySubject<List<Rsvp>> = new ReplaySubject<List<Rsvp>>(1);
  private searchResultsPage: BehaviorSubject<Page<Rsvp>> = new BehaviorSubject(Page.empty<Rsvp>());

  constructor(private rsvpHttpService: RsvpHttpService,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService) {
  }

  get searchResults(): Observable<Page<Rsvp>> {
    return this.searchResultsPage.asObservable();
  }

  get rsvps(): Observable<List<Rsvp>> {
    return this.rsvpSubject.asObservable().map(rsvpList => rsvpList.sort(
      (rsvp1: Rsvp, rsvp2: Rsvp) => ComparatorUtils.compareRsvps(rsvp1, rsvp2)).toList());
  }

  get totalNights(): Observable<number> {
    return this.rsvps.map((rsvpList: List<Rsvp>): number => {
      return rsvpList
        .map(rsvp => rsvp.nights)
        .reduce((sum: number, current: number) => !!current ? sum + current : sum, 0);
    });
  }

  get totalRevenue(): Observable<number> {
    return this.rsvps.map(rsvpList => {
      return rsvpList
        .map(rsvp => rsvp.revenue)
        .reduce((sum: number, currentRevenue: number) => !!currentRevenue ? sum + currentRevenue : sum, 0);
    });
  }

  get totalCommission(): Observable<number> {
    return this.rsvps.map(rsvpList => {
      return rsvpList
        .map(rsvp => rsvp.commission)
        .reduce((sum: number, current: number) => !!current ? sum + current : sum, 0);
    });
  }

  addRsvp(rsvp: Rsvp): Observable<any> {
    return this.rsvpHttpService.add(rsvp)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_RSVP', `Error adding rsvp: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.RSVPS.RSVP_ADDED');
        return result;
      });
  }

  importRsvpFile(eventId: number, file: File): Observable<any> {
    return this.rsvpHttpService
      .importRsvpFile(eventId, file);
  }

  deleteRsvp(rsvp: Rsvp): Observable<any> {
    return this.rsvpHttpService.delete(rsvp)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.DELETE_RSVP', `Error adding rsvp: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.RSVPS.RSVP_DELETED');
        return result;
      });
  }

  updateRsvp(rsvp: Rsvp): Observable<any> {
    return this.rsvpHttpService.update(rsvp)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_RSVP', `Error updating rsvp: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success(
          'EVENT.RSVPS.RSVP_UPDATED', null,
          { lastName: String(rsvp.lastName), firstName: String(rsvp.firstName) });
        return result;
      });
  }

  /**
   * Has to be called to retrieve RSVPs.
   * Is called by resolver.
   *
   * @param eventId
   * @returns RSVP list
   */
  loadRsvps(eventId: number): Observable<List<Rsvp>> {
    return this.rsvpHttpService.getRsvps(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RSVPS',
          `Error retrieving RSVPs: ${ error }`
        );
        return Observable.throw(error);
      })
      .map((rsvpList: List<Rsvp>) => {
        this.rsvpSubject.next(rsvpList);
        return rsvpList;
      });
  }

  /**
   * Loads the RSVPs paginated
   *
   * @param eventId the id of the event
   * @param page the page to be load
   * @param size the number of RSVP to be load
   * @returns an `Page` of RSVP
   */
  loadRsvpsPaginated(eventId: number, page: number, size: number): Observable<Page<Rsvp>> {
    return this.rsvpHttpService.getRsvpsPaginated(eventId, page, size)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RSVPS',
          `Error retrieving RSVPs: ${error}`
        );
        return Observable.throw(error);
      })
      .map((rsvpPage: Page<Rsvp>) => {
        this.searchResultsPage.next(rsvpPage);
        return rsvpPage;
      });
  }

  refreshRsvpsSummary(eventId: number, pagesize: number) {
    this.loadRsvpsPaginated(eventId, Constants.SEARCH_DEFAULT_PAGE, pagesize).take(1).subscribe();
  }

  /**
   * Transform the RSVP list into a room stay summary
   *
   * @param statusCode{ReservationStatusCode[]}
   * @returns {Observable<StaySummary>}
   */
  getRoomStaySummary(): Observable<RoomStaySummary> {
    return this.rsvps.map(rsvpList => {
      return TransactionUtils.transformTransactionsToSummary(rsvpList, this.translateService);
    });
  }

  getRsvpsFromEvent(eventId: number, page: number, size: number): Observable<Page<Rsvp>> {
    return this.rsvpHttpService.getRsvpsFromEvent(eventId, page, size)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RSVPS',
          `Error retrieving RSVPs: ${error}`
        );
        return Observable.throw(error);
      });
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/service/rsvp.store.ts